import { Box, Link, Paper, Typography } from "@mui/material";
import type { ReactElement, ReactNode } from "react";
import { useCallback, useId, useMemo, useState } from "react";
import { useTranslate } from "react-admin";
import { Link as RouterLink } from "react-router-dom";
import type { LabelProps, PieLabelRenderProps } from "recharts";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  BLANK_CATEGORY_KEY,
  type ChartCategory,
  darkenColor,
  type DonutData,
  type DonutSlice,
  formatAxisTick,
  formatChartValue,
  formatCompactCount,
  formatCount,
  formatPeriodLong,
  formatPeriodTick,
  type PeriodGranularity,
  SALESFORCE_CHART_COLORS,
  sliceDisplayLabel,
  type StackedPeriod,
  type StackedSeries,
  stackRenderOrder,
  toNumberOrNull,
} from "../lib/salesforceDashboard";
import { space } from "../theme/designTokens";
import { SalesforceHoverTooltip, useChartHover } from "./SalesforceHoverTooltip";

const CARD_HEIGHT_PX = 344;
const CARD_PADDING_PX = 16;
const LEGEND_ROW_HEIGHT_PX = 22;
const LEGEND_WRAPPED_LINES = 2;
const SWATCH_SIZE_PX = 10;
const MIN_SLICE_LABEL_ANGLE_DEG = 12;
const MIN_INSIDE_LABEL_BAR_HEIGHT_PX = 20;
const LABEL_TEXT_INSET_PX = 14;
const LABEL_TEXT_GAP_PX = 4;
export const Y_AXIS_WIDTH_PX = 36;
const MAX_STACKED_BAR_SIZE_PX = 80;
const ANGLED_TICK_MIN_PERIODS = 4;
const DONUT_HOVER_STROKE_PX = 2;
const COLUMN_HOVER_STROKE_PX = 1;

const colors = SALESFORCE_CHART_COLORS;

export const percentFormat = new Intl.NumberFormat("de-DE", {
  style: "percent",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const thinScrollSx = { scrollbarWidth: "thin" } as const;

const SCROLLBAR_THICKNESS_PX = 10;
const SCROLLBAR_THUMB_COLOR = "#c9c9c9";
const SCROLLBAR_TRACK_COLOR = "#f3f3f3";

// A horizontal scrollbar that stays on screen (macOS hides overlay ones), as the grey bar under a Salesforce chart.
// Where the WebKit pseudo-elements are missing (Firefox) the standard properties give the same colours.
export const visibleScrollbarSx = {
  "&::-webkit-scrollbar": { height: `${SCROLLBAR_THICKNESS_PX}px` },
  "&::-webkit-scrollbar-track": {
    backgroundColor: SCROLLBAR_TRACK_COLOR,
    borderRadius: `${SCROLLBAR_THICKNESS_PX / 2}px`,
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: SCROLLBAR_THUMB_COLOR,
    borderRadius: `${SCROLLBAR_THICKNESS_PX / 2}px`,
  },
  "@supports not selector(::-webkit-scrollbar)": {
    scrollbarColor: `${SCROLLBAR_THUMB_COLOR} ${SCROLLBAR_TRACK_COLOR}`,
    scrollbarWidth: "thin",
  },
} as const;

const ellipsisSx = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

// A label that is cut stays cut at its end: clipped text cannot be scrolled, unlike hidden text.
const clippedEllipsisSx = { ...ellipsisSx, overflow: "clip" } as const;

export const CLICKABLE_STYLE = { cursor: "pointer" } as const;

// The hovered bar is drawn in its own colour with every channel halved (see darkenColor) and outlined. This is
// CSS, not state: a chart that re-renders its bars on hover makes Recharts replace their nodes, and a click whose
// mousedown and mouseup land on different nodes never fires.
export const hoveredBarSx = {
  "& .recharts-bar-rectangle:hover .recharts-rectangle": {
    filter: "brightness(0.5)",
    stroke: colors.hoverOutline,
    strokeWidth: `${COLUMN_HOVER_STROKE_PX}px`,
  },
} as const;

export type SalesforceLegendEntry = { key: string; label: string; color: string };

type SalesforceDashboardCardProps = {
  title: string;
  definition: string;
  asOf: string;
  children: ReactNode;
  subtitle?: string;
  // Fills the grid area it sits in instead of the fixed dashboard card height.
  fillHeight?: boolean;
  // A link under the card's footer, at its bottom right.
  link?: { to: string; label: string };
};

export const SalesforceDashboardCard = ({
  title,
  definition,
  asOf,
  children,
  subtitle,
  fillHeight = false,
  link,
}: SalesforceDashboardCardProps) => {
  const titleId = useId();

  return (
    <Paper
      aria-labelledby={titleId}
      component="section"
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: fillHeight ? "100%" : `${CARD_HEIGHT_PX}px`,
        minHeight: 0,
        minWidth: 0,
        p: `${CARD_PADDING_PX}px`,
        backgroundColor: colors.cardBackground,
        border: `1px solid ${colors.cardBorder}`,
        borderRadius: "4px",
        boxShadow: colors.cardShadow,
        color: colors.text,
      }}
    >
      <Typography
        component="h2"
        id={titleId}
        sx={{
          ...ellipsisSx,
          mb: `${space.md}px`,
          fontSize: "16px",
          fontWeight: 400,
          lineHeight: "20px",
          color: colors.text,
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          sx={{ ...ellipsisSx, mt: `-${space.sm}px`, mb: `${space.md}px`, fontSize: "12px", color: colors.textWeak }}
        >
          {subtitle}
        </Typography>
      )}
      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>{children}</Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: `${space.lg}px`,
          mt: `${space.md}px`,
          fontSize: "12px",
        }}
      >
        <Box component="span" sx={{ ...ellipsisSx, minWidth: 0, color: colors.textWeak }} title={definition}>
          {definition}
        </Box>
        <Box component="span" sx={{ flex: "none", whiteSpace: "nowrap", color: colors.text }}>
          {asOf}
        </Box>
      </Box>
      {link && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: `${space.sm}px`, fontSize: "12px" }}>
          <Link component={RouterLink} sx={{ color: colors.link }} to={link.to} underline="hover">
            {link.label}
          </Link>
        </Box>
      )}
    </Paper>
  );
};

export const SalesforceEmptyState = () => {
  const translate = useTranslate();

  return (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        fontSize: "12px",
        color: colors.text,
      }}
    >
      {translate("salesforceDashboard.noChartData")}
    </Box>
  );
};

const LegendSwatch = ({ color }: { color: string }) => (
  <Box
    aria-hidden
    component="span"
    sx={{
      flex: "none",
      width: `${SWATCH_SIZE_PX}px`,
      height: `${SWATCH_SIZE_PX}px`,
      borderRadius: "50%",
      backgroundColor: color,
    }}
  />
);

type SalesforceLegendProps = { title: string; entries: SalesforceLegendEntry[]; width?: string; maxWidth?: string };

type SalesforceVerticalLegendProps = SalesforceLegendProps & {
  // Lets a long label wrap to two lines instead of being cut on one.
  wrapLabels?: boolean;
};

const wrappedLabelSx = {
  display: "-webkit-box",
  overflow: "hidden",
  textAlign: "right",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: LEGEND_WRAPPED_LINES,
} as const;

export const SalesforceVerticalLegend = ({
  title,
  entries,
  width = "50%",
  maxWidth = "320px",
  wrapLabels = false,
}: SalesforceVerticalLegendProps) => (
  <Box sx={{ display: "flex", flexDirection: "column", width, maxWidth, minHeight: 0 }}>
    <Typography
      sx={{ mb: `${space.sm2}px`, fontSize: "12px", textAlign: "right", color: colors.textWeak }}
      variant="body2"
    >
      {title}
    </Typography>
    <Box
      component="ul"
      sx={{ ...thinScrollSx, flex: 1, minHeight: 0, m: 0, p: 0, listStyle: "none", overflowY: "auto" }}
    >
      {entries.map(entry => (
        <Box
          component="li"
          key={entry.key}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: `${space.md}px`,
            ...(wrapLabels ? { minHeight: `${LEGEND_ROW_HEIGHT_PX}px` } : { height: `${LEGEND_ROW_HEIGHT_PX}px` }),
            fontSize: "12px",
            color: colors.text,
            "&:hover": { backgroundColor: colors.legendHover },
          }}
        >
          <Box
            component="span"
            sx={{ ...(wrapLabels ? wrappedLabelSx : clippedEllipsisSx), minWidth: 0 }}
            title={entry.label}
          >
            {entry.label}
          </Box>
          <LegendSwatch color={entry.color} />
        </Box>
      ))}
    </Box>
  </Box>
);

export const SalesforceHorizontalLegend = ({ title, entries }: SalesforceLegendProps) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: `${space.md}px`, minWidth: 0, fontSize: "12px" }}>
    <Box component="span" sx={{ flex: "none", color: colors.text }}>
      {title}
    </Box>
    <Box
      component="ul"
      sx={{
        ...thinScrollSx,
        display: "flex",
        flex: 1,
        alignItems: "center",
        gap: `${space.lg}px`,
        minWidth: 0,
        m: 0,
        p: 0,
        listStyle: "none",
        overflowX: "auto",
      }}
    >
      {entries.map(entry => (
        <Box
          component="li"
          key={entry.key}
          sx={{
            display: "flex",
            flex: "none",
            alignItems: "center",
            gap: `${space.sm}px`,
            whiteSpace: "nowrap",
            color: colors.text,
            "&:hover": { backgroundColor: colors.legendHover },
          }}
        >
          <LegendSwatch color={entry.color} />
          {entry.label}
        </Box>
      ))}
    </Box>
  </Box>
);

// Rotated axis title as HTML: it wraps like Salesforce and stays testable.
export const VerticalAxisTitle = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      flex: "none",
      alignSelf: "stretch",
      maxHeight: "100%",
      mr: `${space.xs}px`,
      overflow: "hidden",
      fontSize: "12px",
      textAlign: "center",
      color: colors.text,
      transform: "rotate(180deg)",
      writingMode: "vertical-rl",
    }}
  >
    {children}
  </Box>
);

export const HorizontalAxisTitle = ({ children }: { children: ReactNode }) => (
  <Box sx={{ flex: "none", py: `${space.sm}px`, fontSize: "12px", textAlign: "center", color: colors.textWeak }}>
    {children}
  </Box>
);

export const axisTickStyle = (fontSize: number, fill: string) => ({ fontSize, fill });

type DonutDatum = { id: string; name: string; count: number; fill: string };

const sliceLabelRenderer =
  (hoveredIndex: number | null, formatValue: (value: number) => string, percentOf?: (value: number) => string) =>
  (props: PieLabelRenderProps): ReactNode => {
    const { cx, cy, midAngle, middleRadius, startAngle, endAngle, value } = props;
    if (
      cx === undefined ||
      cy === undefined ||
      midAngle === undefined ||
      middleRadius === undefined ||
      Math.abs(endAngle - startAngle) < MIN_SLICE_LABEL_ANGLE_DEG
    ) {
      return null;
    }
    const radians = (-midAngle * Math.PI) / 180;
    return (
      <text
        dominantBaseline="central"
        fill={props.index === hoveredIndex ? colors.tooltipText : colors.textStrong}
        fontSize={11}
        textAnchor="middle"
        x={cx + middleRadius * Math.cos(radians)}
        y={cy + middleRadius * Math.sin(radians)}
      >
        {percentOf ? (
          <>
            <tspan dy="-0.5em" x={cx + middleRadius * Math.cos(radians)}>
              {formatValue(value)}
            </tspan>
            <tspan dy="1.2em" x={cx + middleRadius * Math.cos(radians)}>
              {percentOf(value)}
            </tspan>
          </>
        ) : (
          formatValue(value)
        )}
      </text>
    );
  };

type SalesforceDonutChartProps = {
  data: DonutData;
  legendTitle: string;
  onSliceClick?: (slice: DonutSlice) => void;
  // The measure the slices add up, when it is not the record count.
  valueLabel?: string;
  formatValue?: (value: number) => string;
  // Also writes each slice's share of the total on the slice.
  showPercentages?: boolean;
};

export const SalesforceDonutChart = ({
  data,
  legendTitle,
  onSliceClick,
  valueLabel,
  formatValue = formatCount,
  showPercentages = false,
}: SalesforceDonutChartProps) => {
  const translate = useTranslate();
  const otherLabel = translate("salesforceDashboard.other");
  const measureLabel = valueLabel ?? translate("salesforceDashboard.recordCount");

  const chartData = useMemo<DonutDatum[]>(
    () =>
      data.slices.map(slice => ({
        id: slice.key,
        name: sliceDisplayLabel(slice, otherLabel),
        count: slice.count,
        fill: slice.color,
      })),
    [data.slices, otherLabel]
  );
  const ringTotal = useMemo(() => data.slices.reduce((sum, slice) => sum + slice.count, 0), [data.slices]);
  const legendEntries = useMemo<SalesforceLegendEntry[]>(
    () => chartData.map(datum => ({ key: datum.id, label: datum.name, color: datum.fill })),
    [chartData]
  );

  const { hover, show, hide } = useChartHover();
  const [hoverTarget, setHoverTarget] = useState<number | null>(null);
  const hoveredIndex = hover ? hoverTarget : null;
  const renderLabel = useMemo(
    () =>
      sliceLabelRenderer(
        hoveredIndex,
        formatValue,
        showPercentages ? (value: number) => percentFormat.format(value / ringTotal) : undefined
      ),
    [hoveredIndex, formatValue, showPercentages, ringTotal]
  );
  const showSlice = useCallback(
    (index: number, event: React.MouseEvent) => {
      const datum = chartData[index];
      if (!datum) return;
      setHoverTarget(index);
      show(event, {
        rows: [{ label: legendTitle, value: datum.name, color: datum.fill }],
        metric: { label: measureLabel, value: formatValue(datum.count) },
        share: translate("salesforceDashboard.tooltip.share", {
          percent: percentFormat.format(datum.count / ringTotal),
          total: formatValue(ringTotal),
        }),
      });
    },
    [chartData, legendTitle, ringTotal, show, translate, measureLabel, formatValue]
  );
  const clearSlice = useCallback(() => {
    setHoverTarget(null);
    hide();
  }, [hide]);

  if (chartData.length === 0) return <SalesforceEmptyState />;

  return (
    <Box sx={{ display: "flex", flex: 1, minWidth: 0, minHeight: 0, gap: `${space.md}px` }}>
      <Box sx={{ display: "flex", flex: 1, flexDirection: "column", minWidth: 0 }}>
        <Box sx={{ mb: `${space.sm}px`, fontSize: "12px", textAlign: "center", color: colors.text }}>
          {measureLabel}
        </Box>
        <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>
          <ResponsiveContainer height="100%" width="100%">
            <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <Pie
                cx="50%"
                cy="50%"
                data={chartData}
                dataKey="count"
                endAngle={-270}
                innerRadius="56%"
                isAnimationActive={false}
                label={renderLabel}
                onMouseEnter={(_, index, event) => showSlice(index, event)}
                onMouseLeave={clearSlice}
                onMouseMove={(_, index, event) => showSlice(index, event)}
                labelLine={false}
                nameKey="name"
                onClick={onSliceClick ? (_, index) => onSliceClick(data.slices[index]) : undefined}
                outerRadius="92%"
                startAngle={90}
                style={onSliceClick ? CLICKABLE_STYLE : undefined}
                stroke={colors.sliceStroke}
                strokeWidth={1}
              >
                {chartData.map((datum, index) => (
                  <Cell
                    fill={index === hoveredIndex ? darkenColor(datum.fill) : datum.fill}
                    key={datum.id}
                    stroke={index === hoveredIndex ? colors.hoverOutline : colors.sliceStroke}
                    strokeWidth={index === hoveredIndex ? DONUT_HOVER_STROKE_PX : 1}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              fontSize: "22px",
              color: colors.textStrong,
              pointerEvents: "none",
              transform: "translate(-50%, -50%)",
            }}
          >
            {formatValue(data.total)}
          </Box>
        </Box>
      </Box>
      <SalesforceVerticalLegend entries={legendEntries} title={legendTitle} />
      <SalesforceHoverTooltip hover={hover} />
    </Box>
  );
};

// The key names the group when labels could repeat; a chart that does not need it leaves it out.
export type SalesforceColumnDatum = { label: string; value: number | null; key?: string };

type SalesforceColumnChartProps = {
  showValues?: boolean;
  data: SalesforceColumnDatum[];
  yAxisTitle: string;
  xAxisTitle: string;
  onBarClick?: (datum: SalesforceColumnDatum) => void;
};

const renderColumnValueLabel = (props: LabelProps): ReactElement | null => {
  const value = toNumberOrNull(props.value);
  const x = Number(props.x);
  const y = Number(props.y);
  const width = Number(props.width);
  const height = Number(props.height);
  if (value === null || ![x, y, width, height].every(Number.isFinite)) return null;
  const inside = height >= MIN_INSIDE_LABEL_BAR_HEIGHT_PX;
  return (
    <text
      fill={colors.textStrong}
      fontSize={12}
      textAnchor="middle"
      x={x + width / 2}
      y={inside ? y + LABEL_TEXT_INSET_PX : y - LABEL_TEXT_GAP_PX}
    >
      {formatChartValue(value)}
    </text>
  );
};

export const SalesforceColumnChart = ({
  data,
  yAxisTitle,
  xAxisTitle,
  onBarClick,
  showValues = true,
}: SalesforceColumnChartProps) => {
  const translate = useTranslate();
  const unavailable = data.filter(datum => datum.value === null);

  const { hover, show, hide } = useChartHover();
  const sum = useMemo(() => data.reduce((total, datum) => total + (datum.value ?? 0), 0), [data]);
  const showBar = useCallback(
    (payload: unknown, event: React.MouseEvent) => {
      const datum = data.find(candidate => candidate.label === (payload as SalesforceColumnDatum | undefined)?.label);
      if (!datum || datum.value === null) return;
      show(event, {
        rows: [{ label: xAxisTitle, value: datum.label }],
        metric: { label: yAxisTitle, value: formatChartValue(datum.value), color: colors.singleSeries },
        share:
          sum > 0
            ? translate("salesforceDashboard.tooltip.share", {
                percent: percentFormat.format(datum.value / sum),
                total: formatCompactCount(sum),
              })
            : undefined,
      });
    },
    [data, sum, show, translate, xAxisTitle, yAxisTitle]
  );
  const clearBar = hide;

  // Kept as one element while the pointer moves: Recharts replaces the bars' nodes when a Bar element is new.
  const barElement = useMemo(
    () => (
      <Bar
        dataKey="value"
        fill={colors.singleSeries}
        isAnimationActive={false}
        onMouseEnter={(bar, _, event) => showBar(bar.payload, event)}
        onMouseLeave={clearBar}
        onMouseMove={(bar, _, event) => showBar(bar.payload, event)}
        onClick={
          onBarClick
            ? bar => {
                const datum = data.find(
                  candidate => candidate.label === (bar.payload as SalesforceColumnDatum | undefined)?.label
                );
                if (datum) onBarClick(datum);
              }
            : undefined
        }
        style={onBarClick ? CLICKABLE_STYLE : undefined}
      >
        {data.map(datum => (
          <Cell fill={colors.singleSeries} key={datum.label} strokeWidth={0} />
        ))}
        {showValues && <LabelList content={renderColumnValueLabel} dataKey="value" />}
      </Bar>
    ),
    [data, showBar, clearBar, onBarClick, showValues]
  );

  return (
    <Box sx={{ display: "flex", flex: 1, minWidth: 0, minHeight: 0, ...hoveredBarSx }}>
      <VerticalAxisTitle>{yAxisTitle}</VerticalAxisTitle>
      <Box sx={{ display: "flex", flex: 1, flexDirection: "column", minWidth: 0 }}>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <ResponsiveContainer height="100%" width="100%">
            <BarChart barCategoryGap="66%" data={data} margin={{ top: 16, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid stroke={colors.grid} vertical={false} />
              <XAxis axisLine={false} dataKey="label" tick={axisTickStyle(12, colors.text)} tickLine={false} />
              <YAxis
                axisLine={false}
                tick={axisTickStyle(12, colors.tick)}
                tickFormatter={formatAxisTick}
                tickLine={false}
                width={Y_AXIS_WIDTH_PX}
              />
              {barElement}
            </BarChart>
          </ResponsiveContainer>
        </Box>
        {unavailable.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", gap: `${space.lg}px`, fontSize: "12px" }}>
            {unavailable.map(datum => (
              <span key={datum.label}>{`${datum.label}: ${translate("salesforceDashboard.unavailable")}`}</span>
            ))}
          </Box>
        )}
        <HorizontalAxisTitle>{xAxisTitle}</HorizontalAxisTitle>
      </Box>
      <SalesforceHoverTooltip hover={hover} />
    </Box>
  );
};

type SalesforceStackedColumnChartProps = {
  series: StackedSeries;
  granularity: PeriodGranularity;
  categoryLabel: (value: string | null) => string;
  legendTitle: string;
  xAxisTitle: string;
  onSegmentClick?: (periodStart: string, category: string | null) => void;
  // Click on a period column outside any segment, which is how slivers of one or two pixels stay reachable.
  onPeriodClick?: (periodStart: string) => void;
};

const MONTH_TICK_HEIGHT_PX = 66;
// Salesforce draws three value ticks on the short stacked plots.
const STACKED_Y_TICK_COUNT = 3;
const ANGLED_TICK_HEIGHT_PX = 44;
const FLAT_TICK_HEIGHT_PX = 24;

const xAxisTickLayout = (granularity: PeriodGranularity, periodCount: number) => {
  if (granularity === "month") {
    return { angle: -90, textAnchor: "end", height: MONTH_TICK_HEIGHT_PX } as const;
  }
  if (periodCount > ANGLED_TICK_MIN_PERIODS) {
    return { angle: -45, textAnchor: "end", height: ANGLED_TICK_HEIGHT_PX } as const;
  }
  return { angle: 0, textAnchor: "middle", height: FLAT_TICK_HEIGHT_PX } as const;
};

const periodOf = (payload: unknown): StackedPeriod | null => {
  const candidate = payload as Partial<StackedPeriod> | undefined;
  return typeof candidate?.periodStart === "string" ? (candidate as StackedPeriod) : null;
};

const categoryKey = (value: string | null): string => value ?? BLANK_CATEGORY_KEY;

export const SalesforceStackedColumnChart = ({
  series,
  granularity,
  categoryLabel,
  legendTitle,
  xAxisTitle,
  onSegmentClick,
  onPeriodClick,
}: SalesforceStackedColumnChartProps) => {
  const translate = useTranslate();

  const legendEntries = useMemo<SalesforceLegendEntry[]>(
    () =>
      series.categories.map(category => ({
        key: categoryKey(category.value),
        label: categoryLabel(category.value),
        color: category.color,
      })),
    [series.categories, categoryLabel]
  );

  const { hover, show, hide } = useChartHover();
  const [hoverTarget, setHoverTarget] = useState<{ category: string; periodStart: string } | null>(null);
  const hovered = hover ? hoverTarget : null;
  const showSegment = useCallback(
    (payload: unknown, category: ChartCategory, event: React.MouseEvent) => {
      const period = periodOf(payload);
      const count = period?.counts[series.categories.indexOf(category)];
      if (!period || !count) return;
      const periodLabel = formatPeriodLong(period.periodStart, granularity);
      setHoverTarget({ category: categoryKey(category.value), periodStart: period.periodStart });
      show(event, {
        rows: [
          { label: xAxisTitle, value: periodLabel },
          { label: legendTitle, value: categoryLabel(category.value), color: category.color },
        ],
        metric: { label: translate("salesforceDashboard.recordCount"), value: formatCount(count) },
        share: translate("salesforceDashboard.tooltip.shareOfPeriod", {
          percent: percentFormat.format(count / period.total),
          total: formatCount(period.total),
          period: periodLabel,
        }),
      });
    },
    [series.categories, granularity, xAxisTitle, legendTitle, categoryLabel, show, translate]
  );
  const clearSegment = useCallback(() => {
    setHoverTarget(null);
    hide();
  }, [hide]);

  const bars = useMemo(
    () =>
      stackRenderOrder(series.categories).map(({ category, index }) => (
        <Bar
          dataKey={(row: StackedPeriod) => row.counts[index]}
          fill={category.color}
          isAnimationActive={false}
          key={categoryKey(category.value)}
          maxBarSize={MAX_STACKED_BAR_SIZE_PX}
          onClick={
            onSegmentClick
              ? bar => {
                  // Recharts omits zero-height bars, so the click index is not the period index; read the row instead.
                  const period = periodOf(bar.payload);
                  if (period) onSegmentClick(period.periodStart, category.value);
                }
              : undefined
          }
          onMouseEnter={(bar, _, event) => showSegment(bar.payload, category, event)}
          onMouseLeave={clearSegment}
          onMouseMove={(bar, _, event) => showSegment(bar.payload, category, event)}
          stackId="periods"
          style={CLICKABLE_STYLE}
        >
          {series.periods.map(period => {
            const isHovered =
              hovered?.category === categoryKey(category.value) && hovered.periodStart === period.periodStart;
            return <Cell fill={isHovered ? darkenColor(category.color) : category.color} key={period.periodStart} />;
          })}
        </Bar>
      )),
    [series.categories, series.periods, hovered, onSegmentClick, showSegment, clearSegment]
  );

  const handleChartClick = useCallback(
    (state: { activeLabel?: string | number }, event: { target: EventTarget | null }) => {
      // A click on a segment is handled by that bar; only the empty part of the column comes here.
      if (event.target instanceof Element && event.target.closest(".recharts-bar-rectangle")) return;
      if (state.activeLabel !== undefined) onPeriodClick?.(String(state.activeLabel));
    },
    [onPeriodClick]
  );

  const tickFormatter = useCallback((value: unknown) => formatPeriodTick(String(value), granularity), [granularity]);

  if (series.periods.length === 0) return <SalesforceEmptyState />;

  const tickLayout = xAxisTickLayout(granularity, series.periods.length);

  return (
    <Box sx={{ display: "flex", flex: 1, minWidth: 0, minHeight: 0 }}>
      <VerticalAxisTitle>{translate("salesforceDashboard.recordCount")}</VerticalAxisTitle>
      <Box sx={{ display: "flex", flex: 1, flexDirection: "column", minWidth: 0 }}>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <ResponsiveContainer height="100%" width="100%">
            <BarChart
              barCategoryGap="18%"
              data={series.periods}
              margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
              onClick={onPeriodClick ? handleChartClick : undefined}
            >
              <CartesianGrid stroke={colors.grid} vertical={false} />
              <XAxis
                angle={tickLayout.angle}
                axisLine={false}
                dataKey="periodStart"
                height={tickLayout.height}
                // Every period is drawn (Salesforce stops after about 38 groups), so the month labels are sampled.
                interval="preserveStart"
                tick={axisTickStyle(11, colors.tick)}
                tickFormatter={tickFormatter}
                tickLine={false}
                textAnchor={tickLayout.textAnchor}
              />
              <YAxis
                allowDecimals={false}
                axisLine={false}
                tick={axisTickStyle(12, colors.tick)}
                tickCount={STACKED_Y_TICK_COUNT}
                tickFormatter={formatAxisTick}
                tickLine={false}
                width={Y_AXIS_WIDTH_PX}
              />
              {/* Keeps the chart tracking the pointer so a click on empty column space knows its period. */}
              <Tooltip content={() => null} cursor={false} />
              {bars}
            </BarChart>
          </ResponsiveContainer>
        </Box>
        <HorizontalAxisTitle>{xAxisTitle}</HorizontalAxisTitle>
        <SalesforceHorizontalLegend entries={legendEntries} title={legendTitle} />
      </Box>
      <SalesforceHoverTooltip hover={hover} />
    </Box>
  );
};
